import {useWbAgentClient} from '@wandb/weave/lib/WbAgent/useWbAgentClient';
import type {TurnResponse} from '@wandb/weave/lib/WbAgent/WbAgentClient';
import {useCallback, useEffect, useRef, useState} from 'react';

import {extractProjectsForThread} from '../../../agentChat/buildThread';
import {extractErrorMessage} from '../../../agentChat/runtime/errors';
import {mergeThreadSummaries} from '../../../agentChat/store/actions';
import {useAgentChatDispatch} from '../../../agentChat/store/context';
import type {ThreadSummary} from '../../../agentChat/store/thread';
import {parseTurnTitle} from '../threadTitle';

const PAGE_SIZE = 50;

/**
 * useThreadHistory manages paginated fetching of root turns for the chat
 * history popover. Provides `loadInitial` (reset + fetch first page),
 * `loadMore` (next page), and `cancelFetch` (abort in-flight requests).
 *
 * Summary thread metadata lands in the agent-chat store via
 * `mergeThreadSummaries`; this hook owns only the ordered list of ids.
 */
export function useThreadHistory() {
  const client = useWbAgentClient();
  const dispatch = useAgentChatDispatch();

  const [ids, setIds] = useState<string[]>([]);
  const [status, setStatus] = useState<'idle' | 'loading' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);

  const abortRef = useRef<AbortController | null>(null);
  const isFetchingRef = useRef(false);
  const offsetRef = useRef(0);

  // Abort any in-flight request on unmount (e.g. navigation away) to avoid
  // firing state updates into a dead component tree.
  useEffect(() => () => abortRef.current?.abort(), []);

  const fetchPage = useCallback(
    async (offset: number) => {
      if (!client) {
        return;
      }

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      isFetchingRef.current = true;

      setStatus('loading');
      setError(null);

      try {
        // TODO: Offset based pagination may result in duplicates or missing
        // results. Add backend support for cursor-based pagination or filtering
        // by a timestamp to resolve.
        const turns = await client.get(
          '/turns',
          {
            // Fetch across all projects the user can read; history is a
            // cross-project view regardless of the current route.
            entity: undefined,
            project: undefined,
            is_root: true,
            sort_by: 'created_at',
            sort_desc: true,
            limit: PAGE_SIZE + 1,
            offset,
          },
          controller.signal
        );

        if (controller.signal.aborted) {
          return;
        }

        const hasMoreResults = turns.length > PAGE_SIZE;
        setHasMore(hasMoreResults);

        const pageTurns = turns.slice(0, PAGE_SIZE);
        const summaries = pageTurns.map(turnToSummary);
        const newIds = summaries.map(s => s.id);

        setIds(prev => {
          const merged = offset === 0 ? newIds : [...prev, ...newIds];
          // Concurrent inserts between page fetches can return the same
          // row in two pages, so duplicates are removed here.
          return Array.from(new Set(merged));
        });

        dispatch(mergeThreadSummaries(summaries));

        offsetRef.current = offset + newIds.length;
        setStatus('idle');
      } catch (err) {
        if ((err as Error).name === 'AbortError') {
          return;
        }

        setError(extractErrorMessage(err));
        setStatus('error');
      } finally {
        // Don't reset if this request was aborted — a newer fetchPage call
        // may have already set isFetchingRef and this must not overwrite it.
        if (!controller.signal.aborted) {
          isFetchingRef.current = false;
        }
      }
    },
    [client, dispatch]
  );

  const cancelFetch = useCallback(() => abortRef.current?.abort(), []);

  const loadInitial = useCallback(() => {
    offsetRef.current = 0;
    setIds([]);
    setHasMore(true);
    fetchPage(0);
  }, [fetchPage]);

  const loadMore = useCallback(() => {
    if (isFetchingRef.current || !hasMore) {
      return;
    }

    fetchPage(offsetRef.current);
  }, [fetchPage, hasMore]);

  return {
    ids,
    status,
    error,
    hasMore,
    loadInitial,
    loadMore,
    cancelFetch,
  };
}

function turnToSummary(turn: TurnResponse): ThreadSummary {
  const {title, promptSnippet} = parseTurnTitle(turn);
  return {
    id: turn.id,
    rootTurnId: turn.id,
    title,
    promptSnippet,
    updatedAt: Date.parse(turn.updated_at),
    createdAt: Date.parse(turn.created_at),
    projects: extractProjectsForThread(turn),
  };
}
